import {
  AfterViewChecked,
  Component,
  DOCUMENT,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, take } from 'rxjs';
import { ISavedQuote } from '../../models/savedQuotes.models';
import { IExtendedTranscriptChunk } from '../../models/transcription.models';
import { SavedQuotesService } from '../../services/savedQuote.service';
import { TranscriptChunkDownloadService } from '../../services/transcriptChunkDownload.service';

@Component({
  selector: 'app-transcription-playback',
  templateUrl: './transcription-playback.component.html',
  styleUrls: ['./transcription-playback.component.scss'],
  standalone: false,
})
export class TranscriptionPlaybackComponent
  implements OnChanges, AfterViewChecked
{
  @Input() transcriptChunks: IExtendedTranscriptChunk[] = [];
  @Input() audioData?: string;
  @Input() fileName: string;
  @Input() audioStartTime = 0;
  @ViewChild('audioPlayer') audioPlayer: ElementRef<HTMLAudioElement>;

  savedQuoteIds$ = this.savedQuotesService.savedQuoteIds$;

  private pendingAudioReload = false;

  autoScroll = true;
  enhancedPlayback = true;

  get showPlaybackLanguage() {
    return this.transcriptChunks.some(
      (c) => c.normalisedText && c.normalisedText !== c.text
    );
  }

  get hasAudio() {
    return !!this.audioData;
  }

  constructor(
    private savedQuotesService: SavedQuotesService,
    private transcriptChunkDownloadService: TranscriptChunkDownloadService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const audioDataChange = changes.audioData;

    if (
      audioDataChange &&
      !audioDataChange.firstChange &&
      audioDataChange.previousValue !== audioDataChange.currentValue
    ) {
      this.pendingAudioReload = true;
    }
  }

  ngAfterViewChecked() {
    if (!this.pendingAudioReload || !this.audioPlayer) {
      return;
    }

    this.pendingAudioReload = false;
    this.audioPlayer.nativeElement.load();
    this.audioPlayer.nativeElement.currentTime = this.audioStartTime;
  }

  updateQuote(chunk: IExtendedTranscriptChunk) {
    if (chunk.loading) {
      return;
    }

    chunk.loading = true;

    this.savedQuotesService.savedQuoteIds$
      .pipe(take(1))
      .subscribe((savedQuoteIds) => {
        const method: Observable<ISavedQuote | void> = savedQuoteIds.has(
          chunk.transcriptChunkId
        )
          ? this.savedQuotesService.deleteQuote(chunk.transcriptChunkId)
          : this.savedQuotesService.saveQuote(chunk.transcriptChunkId);

        method.subscribe(() => {
          chunk.loading = false;
        });
      });
  }

  downloadTranscriptChunk(chunk: IExtendedTranscriptChunk) {
    if (chunk.downloading) {
      return;
    }

    chunk.downloading = true;

    const [, hrs, mins, secs] = chunk.timestampStartTimestamp.match(
      /^(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/
    );

    this.transcriptChunkDownloadService
      .downloadFile(
        chunk.audioChunkFileKey,
        `${this.fileName} - ${hrs}-${mins}-${secs}.m4a`
      )
      .subscribe(() => {
        chunk.downloading = false;
      });
  }

  updateTime(event: Event) {
    const target = event.target as HTMLAudioElement;
    const roundedTime = Math.round(target.currentTime * 1000) / 1000;
    this.updateAndScroll(roundedTime);
  }

  trackChunk(index: number, chunk: IExtendedTranscriptChunk) {
    return chunk && chunk.transcriptChunkId != null
      ? chunk.transcriptChunkId
      : index;
  }

  private updateAndScroll(time: number) {
    const currentHighlightedPosition = this.transcriptChunks.findIndex(
      (chunk) => chunk.highlighted
    );

    const newHighlightedPosition = this.transcriptChunks.findIndex(
      (chunk) => chunk.timestampStart <= time && chunk.timestampEnd >= time
    );

    const highlightChanged =
      currentHighlightedPosition !== newHighlightedPosition;

    if (highlightChanged) {
      this.transcriptChunks = this.transcriptChunks.map((chunk, index) => {
        const highlighted = index === newHighlightedPosition;

        return chunk.highlighted === highlighted
          ? chunk
          : { ...chunk, highlighted };
      });
    }

    if (this.autoScroll && highlightChanged && newHighlightedPosition > -1) {
      setTimeout(() => {
        const element = this.document.querySelector('.highlight');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'start',
          });
        }
      });
    }

    if (!time) {
      const element = this.document.querySelector('.transcript-content');
      if (element) {
        element.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }
}
